import React, { useEffect, useRef, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import { ArrowLeft, Download, X, FileText, Loader2 } from "lucide-react";
import { OPEN_FILE_EVENT, openFile } from "@/lib/fileViewer";
import { BACKEND_URL } from "@/lib/api";

if (typeof Promise.withResolvers !== "function") {
  Promise.withResolvers = function withResolvers() {
    let resolve, reject;
    const promise = new Promise((res, rej) => { resolve = res; reject = rej; });
    return { promise, resolve, reject };
  };
}
pdfjs.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.mjs";

const isBackendFile = (href) => !!href && (href.startsWith(`${BACKEND_URL}/api/`) || href.startsWith("/api/"));

function filenameFrom(res, fallback) {
  const cd = res.headers.get("content-disposition") || "";
  const m = cd.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
  return fallback || (m ? decodeURIComponent(m[1]) : "documento");
}

function usePageWidth(ref, open) {
  const [w, setW] = useState(800);
  useEffect(() => {
    if (!open || !ref.current) return undefined;
    const update = () => setW(Math.min(ref.current.clientWidth - 24, 1000));
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, [open, ref]);
  return w;
}

function Toolbar({ name, blobUrl, onClose }) {
  return (
    <div className="shrink-0 flex items-center gap-2 px-2 sm:px-4 bg-[#1C352D] text-white" style={{ paddingTop: "calc(env(safe-area-inset-top, 0px) + 8px)", paddingBottom: 8 }}>
      <button onClick={onClose} data-testid="file-viewer-back" className="inline-flex items-center gap-1 h-10 px-3 rounded-full hover:bg-white/10 transition-colors duration-150 text-sm">
        <ArrowLeft size={18} /> Indietro
      </button>
      <div className="flex-1 min-w-0 truncate text-sm text-white/90 text-center" data-testid="file-viewer-name">{name}</div>
      {blobUrl && (
        <a href={blobUrl} download={name} data-testid="file-viewer-download" className="h-10 w-10 grid place-items-center rounded-full hover:bg-white/10 transition-colors duration-150" title="Scarica">
          <Download size={18} />
        </a>
      )}
      <button onClick={onClose} data-testid="file-viewer-close" className="h-10 w-10 grid place-items-center rounded-full hover:bg-white/10 transition-colors duration-150" title="Chiudi">
        <X size={20} />
      </button>
    </div>
  );
}

function Body({ file, width }) {
  const [pages, setPages] = useState(0);
  if (file.error) return <div className="text-sm text-white/80 text-center py-20" data-testid="file-viewer-error">{file.error}</div>;
  if (!file.blob) return <div className="py-20 grid place-items-center text-white/70"><Loader2 className="animate-spin" /></div>;
  const type = file.blob.type || "";
  if (type.includes("pdf")) {
    return (
      <Document file={file.blob} onLoadSuccess={({ numPages }) => setPages(numPages)}
        loading={<div className="py-20 grid place-items-center text-white/70"><Loader2 className="animate-spin" /></div>}
        error={<div className="text-sm text-white/80 text-center py-20">Impossibile mostrare il PDF. Usa il pulsante Scarica.</div>}>
        {Array.from({ length: pages }, (_, i) => (
          <div key={i} className="mb-3 shadow-lg bg-white" data-testid={`file-viewer-page-${i + 1}`}>
            <Page pageNumber={i + 1} width={width} renderAnnotationLayer={false} renderTextLayer={false} />
          </div>
        ))}
      </Document>
    );
  }
  if (type.startsWith("image/")) return <img src={file.blobUrl} alt={file.name} className="max-w-full h-auto bg-white" data-testid="file-viewer-image" />;
  return (
    <div className="text-center py-20 text-white/85" data-testid="file-viewer-unsupported">
      <FileText size={36} className="mx-auto mb-3 opacity-70" />
      <div className="text-sm mb-4">Anteprima non disponibile per questo tipo di file.</div>
      <a href={file.blobUrl} download={file.name} className="inline-flex items-center gap-2 rounded-full bg-[#E2725B] px-5 py-2.5 text-sm font-medium">
        <Download size={16} /> Scarica il file
      </a>
    </div>
  );
}

export default function FileViewer() {
  const [file, setFile] = useState(null);
  const scrollRef = useRef(null);
  const width = usePageWidth(scrollRef, !!file);

  const close = () => {
    if (window.history.state?.fileViewer) window.history.back();
    else setFile(null);
  };

  useEffect(() => {
    const onOpen = async (e) => {
      const { url, name } = e.detail;
      setFile({ url, name: name || "Documento" });
      if (!window.history.state?.fileViewer) window.history.pushState({ ...(window.history.state || {}), fileViewer: true }, "");
      try {
        const res = await fetch(url);
        if (!res.ok) throw new Error();
        const blob = await res.blob();
        setFile((f) => f && f.url === url ? { ...f, name: filenameFrom(res, name), blob, blobUrl: URL.createObjectURL(blob) } : f);
      } catch {
        setFile((f) => f && { ...f, error: "Impossibile aprire il file." });
      }
    };
    const onPop = () => setFile(null);
    const onClick = (e) => {
      const a = e.target.closest?.("a[href]");
      if (!a || a.hasAttribute("download") || e.defaultPrevented || e.metaKey || e.ctrlKey) return;
      const href = a.getAttribute("href");
      if (a.target === "_blank" && isBackendFile(href)) { e.preventDefault(); openFile(href, a.dataset.filename); }
    };
    window.addEventListener(OPEN_FILE_EVENT, onOpen);
    window.addEventListener("popstate", onPop);
    document.addEventListener("click", onClick);
    return () => {
      window.removeEventListener(OPEN_FILE_EVENT, onOpen);
      window.removeEventListener("popstate", onPop);
      document.removeEventListener("click", onClick);
    };
  }, []);

  useEffect(() => {
    if (!file) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") close(); };
    window.addEventListener("keydown", onKey);
    return () => { document.body.style.overflow = prev; window.removeEventListener("keydown", onKey); };
  }, [file]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!file) return null;
  return (
    <div className="fixed inset-0 z-[100] flex flex-col bg-stone-800" data-testid="file-viewer" role="dialog" aria-label={file.name}>
      <Toolbar name={file.name} blobUrl={file.blobUrl} onClose={close} />
      <div ref={scrollRef} className="flex-1 overflow-auto flex flex-col items-center p-3" style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 12px)" }}>
        <Body key={file.url} file={file} width={width} />
      </div>
    </div>
  );
}
